import { Grid, IconButton, Menu, MenuProps, Typography, withStyles } from '@material-ui/core';
import InsertInvitationIcon from '@material-ui/icons/InsertInvitation';
import { TextInput } from '@ncr-retail/base-retail-components';
import moment from 'moment';
import React, { useState } from 'react';
import { DateRange as ReactDateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useTranslation } from 'react-i18next';
import BaseButton from '../../common/components/base-button';
import { vw } from '../../common/helpers/dimension-helper';
import { themeColor } from '../../mui-theme';
import {
  getRangeDates,
  handleDateRangeSelect,
  isReceiptLookupPeriodGreaterThanAMonth,
  isReceiptLookupPeriodGreaterThanAWeek,
  setLastMonth,
  setLastWeek,
  setPolicyMax,
  setYesterday,
} from '../transaction-search-helper/date-range-helper';
import {
  DateRange,
  ReturnPolicyResponse,
} from '../interfaces/get-effective-return-policy-response';
import { ReactComponent as BackIcon } from '../media/back-icon.svg';
import { ReactComponent as ForwardIcon } from '../media/forward-icon.svg';
import '../styles/react-date-range-ncr.styles.css';
import { transactionSearchModalStyles } from '../transaction-based-return-styles';

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #E5E5E5',
    boxShadow: '0px 5px 20px rgba(48, 66, 75, 0.4)',
    '@media (min-width: 1024px) and (min-height: 576px)': {
      marginLeft: vw(-0.55),
    },
    '@media (min-width: 1024px) and (min-height: 768px)': {
      marginLeft: vw(-0.55),
    },
    '@media (min-width: 1280px) and (min-height: 720px)': {
      marginLeft: vw(2.45),
    },
    '@media (min-width: 1366px) and (min-height: 768px)': {
      marginLeft: vw(3.45),
    },
    '@media (min-width: 1600px) and (min-height: 1200px)': {
      marginLeft: vw(5.55),
    },
    '@media (min-width: 1600px) and (min-height: 900px)': {
      marginLeft: vw(5.55),
    },
    '@media (min-width: 1920px) and (min-height: 1080px)': {
      marginLeft: vw(8),
    },
  },
})((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    {...props}
  />
));

const SearchParameterCalendar: React.FC<ICalendarProps> = (props: ICalendarProps): JSX.Element => {
  const classes = transactionSearchModalStyles();
  const { dateRange, setDateRange, effectivePolicy, hidePolicyMaxButton, testIdPrefix } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  const transactionSearchId = `${testIdPrefix}-calendar-input`;

  const handleOpenDateRangePicker = event => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseDateRangePicker = () => {
    setAnchorEl(null);
  };

  const standardDateRangeButtons = () => {
    return (
      <Grid item xs={12}>
        {!hidePolicyMaxButton && (
          <BaseButton
            buttonType='white'
            disabled={!effectivePolicy}
            className={classes.policyButtons}
            onClick={() => setPolicyMax(setDateRange, effectivePolicy)}
            data-testid={'calendar-policy-max'}
          >
            {t('Policy Max')}
          </BaseButton>
        )}
        <BaseButton
          buttonType='white'
          disabled={
            effectivePolicy?.receiptLookupPeriod
              ? !(effectivePolicy.receiptLookupPeriod > 0)
              : false
          }
          className={classes.policyButtons}
          onClick={() => setYesterday(setDateRange)}
          data-testid={'calendar-yesterday'}
        >
          {t('Yesterday')}
        </BaseButton>
        <BaseButton
          buttonType='white'
          className={classes.policyButtons}
          disabled={
            effectivePolicy?.receiptLookupPeriod
              ? !isReceiptLookupPeriodGreaterThanAWeek(effectivePolicy?.receiptLookupPeriod)
              : false
          }
          onClick={() => setLastWeek(setDateRange)}
          data-testid={'calendar-last-week'}
        >
          {t('Last Week')}
        </BaseButton>
        <BaseButton
          buttonType='white'
          disabled={
            effectivePolicy?.receiptLookupPeriod
              ? !isReceiptLookupPeriodGreaterThanAMonth(effectivePolicy?.receiptLookupPeriod)
              : false
          }
          className={classes.policyButtons}
          onClick={() => setLastMonth(setDateRange)}
          data-testid={'calendar-last-month'}
        >
          {t('Last Month')}
        </BaseButton>
      </Grid>
    );
  };

  const isInvalidDate = date => {
    const dateObj = moment(date);
    if (effectivePolicy) {
      const startDate = moment().subtract(effectivePolicy.receiptLookupPeriod + 1, 'd');
      if (dateObj.isAfter(moment()) || dateObj.isBefore(startDate)) {
        return true;
      }
    } else {
      if (dateObj.isAfter(moment())) {
        return true;
      }
    }
  };

  const renderTopNavSection = (focusedDate: Date, changeShownDate: Function) => {
    const momentDate = moment(focusedDate);
    const month = momentDate.format('MMMM');
    const year = momentDate.format('YYYY');
    return (
      <Grid
        container
        direction='row'
        justifyContent='space-between'
        className={classes.calendarNavHeader}
      >
        <Grid item xs={6}>
          <Typography className={classes.calendarNavMonthYear}>
            {month} {year}
          </Typography>
        </Grid>
        <Grid
          xs={6}
          container
          item
          direction={'row'}
          justifyContent={'flex-end'}
          className={classes.calendarNavButtonHead}
        >
          <Grid item xs={6} className={classes.calendarNavBack}>
            <IconButton
              className={classes.calendarMonthNavButtons}
              onClick={() => changeShownDate(-1, 'monthOffset')}
            >
              <BackIcon className={classes.iconStyle} />
            </IconButton>
          </Grid>
          <Grid item xs={6} className={classes.calendarNavForward}>
            <IconButton
              className={classes.calendarMonthNavButtons}
              onClick={() => changeShownDate(1, 'monthOffset')}
            >
              <ForwardIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={4} className={classes.labelHeadTop}>
        <Typography className={classes.dateLabel}>Date</Typography>
      </Grid>
      <Grid item xs={8}>
        <Grid item>
          <TextInput
            name={transactionSearchId}
            type={'standard'}
            value={getRangeDates(dateRange)}
            onFocus={e => {
              e.preventDefault();
            }}
            onClick={e => {
              e.preventDefault();
              handleOpenDateRangePicker(e);
            }}
            onChange={e => {
              e.preventDefault();
            }}
            className={classes.inputText}
            autoComplete='off'
            inputProps={{
              'data-testid': `${transactionSearchId}-input-field`,
              'data-keyboard-owner': `${transactionSearchId}-focus`,
              'data-id': 1,
            }}
            id={transactionSearchId}
            inputIcon={{ icon: <InsertInvitationIcon /> }}
          />
        </Grid>
        <StyledMenu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCloseDateRangePicker}
        >
          {dateRange && (
            <ReactDateRange
              showMonthAndYearPickers={false}
              showSelectionPreview={false}
              showDateDisplay={false}
              showPreview={false}
              weekdayDisplayFormat={'EEEEE'}
              ranges={[dateRange]}
              color={themeColor.passiveGreen}
              onChange={range => handleDateRangeSelect(range, setDateRange)}
              disabledDay={date => isInvalidDate(date)}
              navigatorRenderer={renderTopNavSection}
            />
          )}
        </StyledMenu>
        {standardDateRangeButtons()}
      </Grid>
    </>
  );
};

export default SearchParameterCalendar;

export interface ICalendarProps {
  setDateRange: (input: DateRange) => void;
  effectivePolicy?: ReturnPolicyResponse;
  dateRange: DateRange;
  hidePolicyMaxButton: boolean;
  testIdPrefix: string;
}
